<template>
  <div class="project-facts" ref="projectFactsElement">
    <div class="grid">
      <h2 class="title" ref="titleElement">{{ title }}</h2>
      <div class="facts" ref="factsElement">
        <div class="fact" v-for="f in facts" :key="f.id">
          <p class="title">
            {{ f.title }}
          </p>
          <p class="text">
            {{ f.fact }}
          </p>
        </div>
      </div>
      <div ref="contentElement" class="content">
        <StructuredText
          class="text rich-text"
          v-if="description"
          :data="description"
          :render-block="renderBlock"
          :render-link-to-record="renderLinkToRecord"
          :custom-node-rules="customNodeRules"
        />
        <div class="textfields" v-if="textFields.length">
          <div class="textfield" v-for="field in textFields" :key="field.id">
            <h3 class="title" v-if="field.title">{{ field.title }}</h3>
            <div class="text rich-text" v-if="field.text" v-html="field.text"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useTextFadeIn from '~/utils/useTextFadeIn';
import { StructuredText } from 'vue-datocms';
import renderBlock from '~/dato/renderBlock';
import renderLinkToRecord from '~/dato/renderLinkToRecord';
import customNodeRules from '~/dato/customNodeRules';

const props = defineProps<{
  title?: string;
  description: null | {
    value: object;
    links?: object[];
  };
  facts: {
    id?: string;
    title?: string;
    fact?: string;
  }[];
  textFields: {
    id?: string;
    title?: string;
    text?: string;
  }[];
}>();

const projectFactsElement = ref<null | HTMLElement>(null);
const titleElement = ref<null | HTMLElement>(null);
const factsElement = ref<null | HTMLElement>(null);
const contentElement = ref<null | HTMLElement>(null);

useTextFadeIn(projectFactsElement, [titleElement, factsElement, contentElement]);
</script>

<style scoped lang="scss">
.project-facts {
  background-color: var(--color-off-white);

  @media (--vs) {
    padding: 3rem var(--base-component-padding-inline);
  }
  @media (--vl) {
    padding: 4rem var(--base-component-padding-inline) 6rem var(--base-component-padding-inline);
  }

  > .grid {
    display: grid;

    @media (--vs) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
      gap: 1.5rem;
    }

    @media (--vl) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 2.5rem 2rem;

      > .title {
        grid-area: 1 / 1 / 2 / 2;
      }

      > .facts {
        grid-area: 2 / 1 / 3 / 2;
      }

      > .content {
        grid-area: 2 / 2 / 4 / 3;
      }
    }

    > .title {
      @include text-2xl(700);

      @media (--vl) {
        padding-bottom: 3.5rem;
      }
    }

    > .facts {
      @media (--vl) {
        position: sticky;
        top: 5.25rem;
      }

      > .fact {
        &:not(:last-of-type) {
          padding-bottom: 1rem;
        }

        > .title {
          @include text-md(300);
        }

        > .text {
          @include text-md(700);
        }
      }
    }

    > .content {
      > .text {
        @include text-md(700);
      }

      > .textfields {
        > .textfield {
          &:not(:last-of-type) {
            padding-bottom: 2.5rem;
          }

          > .title {
            @include text-md(700);

            padding-bottom: 0.5rem;
          }

          > .text {
            @include text-md(300);
          }
        }
      }
    }
  }
}
</style>
